import { useBrowserTabTitle } from "common/hooks/use-browser-tab-title/use-browser-tab-title";
import { useGTM } from "common/hooks/useGTM/useGTM";
import useInitViewer from "common/hooks/useInitViewer/useInitViewer";
import { useLocationChangeListener } from "common/hooks/useLocationChangeListener/useLocationChangeListener";
import { ModalProvider, NotFoundPage, UnauthorizedPage } from "dash-ui";
import ProtectedRoute from "flow-components/routes/ProtectedRoute/ProtectedRoute";
import { RoutesPath } from "flow-components/routes/routes";
import AddAdditionalInfoPage from "pages/authentication/complete-sign-up-scope/AddAdditionalInfoPage/AddAdditionalInfoPage";
import PickSubscriptionPage from "pages/authentication/complete-sign-up-scope/PickSubscriptionPage/PickSubscriptionPage";
import StripeCheckoutResultPage from "pages/authentication/complete-sign-up-scope/StripeCheckoutResultPage/StripeCheckoutResultPage";
import ForgotPasswordPage from "pages/authentication/ForgotPasswordPage/ForgotPasswordPage";
import ResetPasswordPage from "pages/authentication/ResetPasswordPage/ResetPasswordPage";
import SignIn from "pages/authentication/SignInPage/SignInPage";
import DashboardPage from "pages/Dashboard/Dashboard";
import AddPaymentPage from "pages/flow/AddPaymentPage/AddPaymentPage";
import PortfolioDetailsPage from "pages/portfolios/PortfolioDetailsPage/PortfolioDetailsPage";
import PortfolioPreviewPage from "pages/portfolios/PortfolioPreviewPage/PortfolioPreviewPage";
import PortfolioPropertyPreviewPage from "pages/portfolios/PortfolioPropertyPreviewPage/PortfolioPropertyPreviewPage";
import PortfoliosPage from "pages/portfolios/PortfoliosPage/PortfoliosPage";
import UnavailablePortfolioPage from "pages/portfolios/UnavailablePortfolioPage/UnavailablePortfolioPage";
import ProfilePage from "pages/ProfilePage/ProfilePage";
import PropertiesPage from "pages/properties/PropertiesPage/PropertiesPage";
import PropertyPreviewAsClientPage from "pages/properties/PropertyPreviewAsClientPage/PropertyPreviewAsClientPage";
import PropertyPreviewPage from "pages/properties/PropertyPreviewPage/PropertyPreviewPage";
import TrackingPage from "pages/TrackingPage/TrackingPage";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { CodeConfirmationPage } from "v2/pages/auth/code-confirmation/CodeConfirmationPage";
import CompleteSignUpSSOPage from "v2/pages/auth/sign-up/complete-sign-up-sso-page";
import { SignUpPage } from "v2/pages/auth/sign-up/page";
import { SSOCallback } from "v2/pages/auth/sso-callback";
import { IdxDetailsPage } from "v2/pages/idx/idx-details-page";
import { IdxEmbeddedPortfolioContainer } from "v2/pages/idx/idx-embedded-container";
import { IdxEmbeddedPropertyPage } from "v2/pages/idx/idx-embedded-property-page";
import { IdxEmbeddedSearchPage } from "v2/pages/idx/idx-embedded-search-page";
import { IdxPage } from "v2/pages/idx/idx-page";
import PrivacyPolicy from "./pages/flow/PrivacyPolicy/PrivacyPolicy";
import TermConditions from "./pages/flow/TermConditions/TermConditions";

function App() {
  const [isViewerReady] = useInitViewer();
  const navigate = useNavigate();
  useBrowserTabTitle();
  useGTM();
  useLocationChangeListener();

  return isViewerReady ? (
    <Routes>
      <Route
        path="/"
        element={<Navigate replace to={RoutesPath.Dashboard} />}
      />
      <Route
        path={RoutesPath.Dashboard}
        element={
          <ProtectedRoute withBreadcrumbs={false} showContent>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={RoutesPath.AddPayment}
        element={
          <ProtectedRoute withBreadcrumbs={false} showContent>
            <ModalProvider>
              <AddPaymentPage />
            </ModalProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path={RoutesPath.Profile}
        element={
          <ProtectedRoute
            withContainer={false}
            customPermissions={{
              account: true,
              verifiedAccount: true,
              hasSubscription: false,
            }}
          >
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path={RoutesPath.Properties}
        element={
          <ProtectedRoute showContent>
            <PropertiesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={RoutesPath.PropertyPreview}
        element={
          <ProtectedRoute withContainer={false} showContent>
            <PropertyPreviewPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={RoutesPath.PropertyPreviewAsClient}
        element={
          <ProtectedRoute withContainer={false}>
            <PropertyPreviewAsClientPage />
          </ProtectedRoute>
        }
      />

      <Route
        path={RoutesPath.PortfolioPreview}
        element={
          <ModalProvider>
            <PortfolioPreviewPage />
          </ModalProvider>
        }
      />
      <Route
        path={RoutesPath.PropertyInPortfolioPreview}
        element={
          <ModalProvider>
            <PortfolioPropertyPreviewPage isInPortfolioFolder />
          </ModalProvider>
        }
      />
      <Route
        path={RoutesPath.PortfolioPropertyPreview}
        element={
          <ModalProvider>
            <PortfolioPropertyPreviewPage />
          </ModalProvider>
        }
      />

      <Route
        path={RoutesPath.Portfolios}
        element={
          <ProtectedRoute>
            <PortfoliosPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={RoutesPath.PortfolioDetails}
        element={
          <ProtectedRoute>
            <PortfolioDetailsPage />
          </ProtectedRoute>
        }
      />

      <Route path={RoutesPath.SignIn} element={<SignIn />} />
      <Route path={RoutesPath.SignUp} element={<SignUpPage />} />
      <Route
        path={RoutesPath.CompleteSignUpSSO}
        element={
          <ProtectedRoute
            withContainer={false}
            withProfileDropdown={false}
            customPermissions={{
              account: true,
              verifiedAccount: false,
              hasSubscription: false,
            }}
          >
            <CompleteSignUpSSOPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={RoutesPath.CompleteSignUpCodeConfirmation}
        element={
          <ProtectedRoute
            withContainer={false}
            customPermissions={{
              account: true,
              verifiedAccount: false,
              hasSubscription: false,
            }}
          >
            <CodeConfirmationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={RoutesPath.CompleteSignUpPickSubscription}
        element={
          <ProtectedRoute
            withContainer={false}
            customPermissions={{
              account: true,
              verifiedAccount: true,
              hasSubscription: false,
              ssoBlock: true,
            }}
          >
            <ModalProvider>
              <PickSubscriptionPage />
            </ModalProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path={RoutesPath.CompleteSignUpAddAdditionalInfo}
        element={
          <ProtectedRoute
            withBreadcrumbs={false}
            withContainer={false}
            customPermissions={{
              account: true,
              verifiedAccount: true,
              hasSubscription: false,
              ssoBlock: true,
            }}
          >
            <AddAdditionalInfoPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={RoutesPath.StripeCheckoutResult}
        element={
          <ProtectedRoute
            withContainer={false}
            withBreadcrumbs={false}
            customPermissions={{
              account: true,
              verifiedAccount: true,
              hasSubscription: false,
              ssoBlock: true,
            }}
          >
            <StripeCheckoutResultPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={RoutesPath.ForgotPassword}
        element={<ForgotPasswordPage />}
      />
      <Route path={RoutesPath.ResetPassword} element={<ResetPasswordPage />} />
      <Route
        path={RoutesPath.UnavailablePortfolioPage}
        element={<UnavailablePortfolioPage />}
      />
      <Route
        path={RoutesPath.Tracking}
        element={
          <ProtectedRoute showContent>
            <TrackingPage />
          </ProtectedRoute>
        }
      />
      <Route path={RoutesPath.TermConditions} element={<TermConditions />} />
      <Route path={RoutesPath.PrivacyPolicy} element={<PrivacyPolicy />} />
      <Route
        path="*"
        element={
          <NotFoundPage onRedirect={() => navigate(RoutesPath.Dashboard)} />
        }
      />

      {/* IDX Routes */}
      <Route
        path={RoutesPath.Exchange}
        element={
          <ProtectedRoute>
            <IdxPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={RoutesPath.ExchangeDetails}
        element={
          <ProtectedRoute>
            <IdxDetailsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={RoutesPath.ExchangeEmbeddedPortfolio}
        element={<IdxEmbeddedPortfolioContainer />}
      />
      <Route
        path={RoutesPath.ExchangeEmbeddedProperty}
        element={
          <ModalProvider>
            <IdxEmbeddedPropertyPage />
          </ModalProvider>
        }
      />
      <Route
        path={RoutesPath.ExchangeEmbeddedSearch}
        element={
          <ModalProvider>
            <IdxEmbeddedSearchPage />
          </ModalProvider>
        }
      />

      <Route
        path={RoutesPath.ExchangeUnauthorized}
        element={
          <UnauthorizedPage
            onRedirect={() => {
              window.location.href = "https://dashtodash.com/";
            }}
          />
        }
      />

      {/* SSO Routes */}
      <Route path={RoutesPath.SSOCallback} element={<SSOCallback />} />
    </Routes>
  ) : null;
}

export default App;
